/**
 * TikTok Pixel Tracking Utility
 * Pixel ID: D5BT6KRC77UD75S4OG3G
 * 
 * This utility provides methods to track TikTok conversion events
 * throughout the application, particularly for the wizard flow.
 */

const TIKTOK_PIXEL_ID = 'D5BT6KRC77UD75S4OG3G';

/**
 * Check if TikTok Pixel is loaded
 */
const isTikTokPixelLoaded = () => {
    return typeof window !== 'undefined' && window.ttq;
};

/**
 * Track a page view
 */
export const trackPageView = () => {
    if (isTikTokPixelLoaded()) {
        window.ttq.page();
    }
};

/**
 * Track a custom event
 * @param {string} eventName - The event name (e.g., 'CompleteRegistration', 'SubmitForm')
 * @param {Object} eventData - Additional event parameters
 * @param {string} [eventId] - Unique ID for event deduplication
 */
export const trackEvent = (eventName, eventData = {}, eventId = null) => {
    if (isTikTokPixelLoaded()) {
        const properties = { ...eventData };
        if (eventId) {
            properties.event_id = eventId;
        }
        window.ttq.track(eventName, properties, eventId ? { event_id: eventId } : undefined);
    }
};

/**
 * Identify a user for enhanced matching
 * @param {Object} userData - User data for matching (email, phone, etc.)
 */
export const identifyUser = (userData = {}) => {
    if (isTikTokPixelLoaded()) {
        window.ttq.identify(userData);
    }
};

// ============================================
// Wizard-Specific Tracking Events
// ============================================

/**
 * Track when a user starts the wizard
 */
export const trackWizardStart = () => {
    trackEvent('InitiateCheckout', {
        content_type: 'product',
        content_id: 'wizard_start',
        content_name: 'Lead Qualification Wizard',
    });
};

/**
 * Track wizard step progression
 * @param {number} step - Current step number
 * @param {string} stepName - Name of the step
 */
export const trackWizardStep = (step, stepName) => {
    trackEvent('ViewContent', {
        content_type: 'product',
        content_id: `wizard_step_${step}`,
        content_name: stepName,
    });
};

/**
 * Track wizard completion (lead submission)
 * @param {Object} submissionData - Data about the submission
 */
/**
 * Track wizard completion (lead submission)
 * @param {Object} submissionData - Data about the submission
 * @param {string} [eventId] - Unique ID for deduplication
 */
export const trackWizardComplete = (submissionData = {}, eventId = null) => {
    // CompleteRegistration is the standard TikTok event for form completions
    trackEvent('CompleteRegistration', {
        content_type: 'product',
        content_id: 'wizard_complete',
        content_name: 'Lead Qualification Complete',
        value: 1,
        currency: 'USD',
    }, eventId);

    // Also track SubmitForm for redundancy
    trackEvent('SubmitForm', {
        content_type: 'product',
        content_id: 'wizard_form',
        content_name: 'Wizard Submission',
    }, eventId);
};

/**
 * Track CV upload
 */
export const trackCVUpload = () => {
    trackEvent('AddToCart', {
        content_type: 'product',
        content_id: 'cv_upload',
        content_name: 'CV Upload',
    });
};

/**
 * Track contact form submission
 * @param {Object} contactData - Contact information
 * @param {string} [eventId] - Unique ID for deduplication
 */
export const trackContactSubmit = (contactData = {}, eventId = null) => {
    // Identify user for enhanced matching (hashed on TikTok's end)
    if (contactData.email || contactData.phone) {
        identifyUser({
            email: contactData.email,
            phone_number: contactData.phone,
        });
    }

    trackEvent('Contact', {
        content_type: 'product',
        content_id: 'contact_form',
    }, eventId);
};

/**
 * Track consultation booking
 */
export const trackConsultationBooked = () => {
    trackEvent('Schedule', {
        content_type: 'product',
        content_id: 'consultation',
        content_name: 'Free Consultation Booked',
    });
};

export default {
    trackPageView,
    trackEvent,
    identifyUser,
    trackWizardStart,
    trackWizardStep,
    trackWizardComplete,
    trackCVUpload,
    trackContactSubmit,
    trackConsultationBooked,
};
